import { NativeAudio } from '@capacitor-community/native-audio';
import { environment } from '@storees/environments';
import { Capacitor, CapacitorHttp, HttpOptions } from '@capacitor/core';
import { Directory, Filesystem, ReadFileOptions } from '@capacitor/filesystem';
import { Router } from '@angular/router';
import {
  AudioAssetId,
  LOADING_SCREEN,
  STORIES_FILESYSTEM_PATH,
} from '@storees/shared-constants';
import { AlertController } from '@ionic/angular/standalone';
import { type components, NarratorVoice } from '@storees/api-types';
import { NgZone } from '@angular/core';
import { StoreesSubscriptionService } from '../services/storees-subscription-service';
import { FirebaseAnalyticsService } from '../services/firebase/FirebaseAnalyticsService';
import { AuthService } from '../services/auth-service';
import { DexieProfilesService } from '../services/dexie/dexie-profiles.service';
import { ProfileService } from '../services/profile-service';

type Narrator = components['schemas']['Narrator'];

const { baseDomainName } = environment;

let currentPlayingAssetId: string | null = null;

export const PRELOADED_AUDIO_ASSET_IDS: string[] = [
  AudioAssetId.AF_BELLA_INTRO,
  AudioAssetId.AF_HEART_INTRO,
  AudioAssetId.AF_NICOLE_INTRO,
  AudioAssetId.BF_EMMA_INTRO,
  AudioAssetId.FF_SIWIS_INTRO,
];

export async function preloadAudio(
  assetId: string,
  assetPath: string,
  audioChannelNum?: number,
  isUrl?: boolean
) {
  try {
    await NativeAudio.preload({
      assetId,
      assetPath,
      audioChannelNum,
      isUrl, // pass true if assetPath is a `file://` url
    });
  } catch (error) {
    console.error('Error preloading audio:', error);
  }
}

export async function playAudio(
  assetId: string,
  onEnd?: () => void
): Promise<void> {
  try {
    // Add listener for the complete event if callback is provided
    if (onEnd) {
      await NativeAudio.addListener('complete', (data) => {
        // Check if the completed audio matches the one we're tracking
        if (data.assetId === assetId) {
          onEnd();
        }
      });
    }

    await NativeAudio.play({
      assetId,
    });
  } catch (error) {
    console.error('Error playing audio:', error);
    throw error;
  }
}

export async function stopAudio(assetId: string) {
  await NativeAudio.stop({
    assetId,
  });
  console.log('Stopping Audio');
}

export async function togglePause(isPaused: boolean, assetId: string) {
  if (isPaused) {
    await NativeAudio.resume({
      assetId,
    });
  } else {
    await NativeAudio.pause({
      assetId,
    });
  }
}

export async function unloadAudio(assetId: string) {
  await NativeAudio.unload({
    assetId,
  });
}

/**
 * Maps narrator voice to the corresponding audio asset ID
 */
export function getAudioAssetIdForNarrator(
  narratorVoice: string,
  narrators: Narrator[]
): string | null {
  // Find the narrator to get the voice enum value
  const narrator = narrators.find((n) => n.id === narratorVoice);
  if (!narrator) {
    console.warn('Narrator not found for voice:', narratorVoice);
    return null;
  }

  // Map narrator voice enum to audio asset ID
  const voiceToAssetMap: Record<string, string> = {
    [NarratorVoice.af_heart]: AudioAssetId.AF_HEART_INTRO,
    [NarratorVoice.af_bella]: AudioAssetId.AF_BELLA_INTRO,
    [NarratorVoice.af_nicole]: AudioAssetId.AF_NICOLE_INTRO,
    [NarratorVoice.bf_emma]: AudioAssetId.BF_EMMA_INTRO,
    [NarratorVoice.ff_siwis]: AudioAssetId.FF_SIWIS_INTRO,
  };

  const assetId = voiceToAssetMap[narrator.voice];

  // Verify the asset ID exists in the preloaded assets
  if (assetId && PRELOADED_AUDIO_ASSET_IDS.includes(assetId)) {
    return assetId;
  }

  console.warn('Audio asset not found for narrator voice:', narrator.voice);
  return null;
}

/**
 * Plays audio for the selected narrator voice
 */
export async function playNarratorAudio(
  selectedNarratorVoice: string | undefined,
  narrators: Narrator[],
  ngZone: NgZone
): Promise<boolean> {
  if (!selectedNarratorVoice) {
    console.warn('No narrator voice selected');
    return false;
  }

  const assetId = getAudioAssetIdForNarrator(selectedNarratorVoice, narrators);
  if (!assetId) {
    console.warn('No audio asset found for selected narrator');
    return false;
  }

  try {
    currentPlayingAssetId = assetId;

    // Play audio with callback to reset state when finished
    await playAudio(assetId, () => {
      ngZone.run(() => {
        currentPlayingAssetId = null;
        console.log('Audio completed, reset play button state');
        // Dispatch a custom event to notify the component
        window.dispatchEvent(
          new CustomEvent('narratorAudioComplete', {
            detail: { narratorId: selectedNarratorVoice },
          })
        );
      });
    });

    console.log('Playing narrator audio:', assetId);
    return true; // Return true when audio starts successfully
  } catch (error) {
    console.error('Error playing narrator audio:', error);
    currentPlayingAssetId = null;
    return false;
  }
}

// Add this export to make currentPlayingAssetId accessible
export function getCurrentPlayingAssetId(): string | null {
  return currentPlayingAssetId;
}

// Modify stopCurrentAudio to return actual state
export async function stopCurrentAudio(): Promise<boolean> {
  let wasPlaying = false;
  if (currentPlayingAssetId) {
    wasPlaying = true;
    try {
      await stopAudio(currentPlayingAssetId);
      console.log('Stopped narrator audio:', currentPlayingAssetId);
    } catch (error) {
      console.error('Error stopping narrator audio:', error);
    } finally {
      currentPlayingAssetId = null;
    }
  }
  return !wasPlaying; // Return false if audio was successfully stopped
}

/**
 * Stops the currently playing narrator audio
 */
// export async function stopCurrentAudio(): Promise<boolean> {
//   let isPlaying = false;
//   if (currentPlayingAssetId) {
//     try {
//       await stopAudio(currentPlayingAssetId);
//       console.log('Stopped narrator audio:', currentPlayingAssetId);
//     } catch (error) {
//       console.error('Error stopping narrator audio:', error);
//     } finally {
//       currentPlayingAssetId = null;
//       isPlaying = false;
//     }
//   }
//   return isPlaying;
// }

/**
 * Deletes all local profiles and resyncs them, put here as it causes injection issues in AuthService
 */
export async function deleteLocalProfilesAndResync(profileService: ProfileService,
                                                   dexieProfilesService: DexieProfilesService,
                                                   authService: AuthService) {
  await profileService.deleteAllProfilesFromLocal();
  await authService.updateSessionJWT();
  const jwtToken = await authService.getSessionJwtToken();
  
  try {
    await dexieProfilesService.syncProfiles(jwtToken);
  } catch (error) {
    console.error('deleteLocalProfilesAndResync - Profile sync failed, continuing with local data:', error);
  }
}

/**
 * This is for local development on mobile devices with Live Reload that are using CapactiorHttp calls
 * ORY tunnel must be running on the host machine that the mobile device is connecting to
 * @param baseUrl
 * @param stackName
 */
export function useOryBaseUrlIfNecessary(baseUrl: string, stackName: string) {
  console.log('basePath', baseUrl);
  console.log('stackName', stackName);
  if (stackName === 'dev' && Capacitor.isNativePlatform()) {
    baseUrl = `https://auth.staging.${baseDomainName}`;
  }
  return baseUrl;
}

// Helper method to load image from filesystem
export async function loadFileFromFileSystem(
  path: string,
  mimeType: string,
  directory?: Directory
) {
  let storyImageDataUrl = '';
  console.log(
    `Attempting to read file from path: ${path}, directory: ${
      directory || 'none'
    }`
  );

  try {
    const options: ReadFileOptions = { path };
    if (directory) {
      options.directory = directory;
    }

    const fileContents = await Filesystem.readFile(options);
    console.log('Read image file successfully');

    // Create a proper data URL from the base64 data
    // Update the template binding to use the safe URL
    storyImageDataUrl = `${mimeType},${fileContents.data}`;

    console.log('Image loaded from filesystem and converted to data URL');
  } catch (error) {
    console.error('Failed to read file:', error);
    throw error;
  }

  return storyImageDataUrl;
}

const fileSourceCache = new Map<string, Promise<string>>();

function getFileSourceCacheKey(path: string, directory?: Directory): string {
  return `${directory || 'none'}:${path}`;
}

export async function getFileSourceFromFileSystem(
  path: string,
  mimeType: string,
  directory?: Directory
): Promise<string> {
  const cacheKey = getFileSourceCacheKey(path, directory);
  const cached = fileSourceCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const sourcePromise = (async () => {
    if (Capacitor.isNativePlatform() && directory) {
      const { uri } = await Filesystem.getUri({ path, directory });
      return Capacitor.convertFileSrc(uri);
    }

    return loadFileFromFileSystem(path, mimeType, directory);
  })().catch((error) => {
    fileSourceCache.delete(cacheKey);
    throw error;
  });

  fileSourceCache.set(cacheKey, sourcePromise);
  return sourcePromise;
}

export function clearFileSourceCache(path: string, directory?: Directory): void {
  fileSourceCache.delete(getFileSourceCacheKey(path, directory));
}

export type NavigateToLoadingScreenOptions = {
  navigationState?: Record<string, unknown>;
};

export async function navigateToLoadingScreen(
  router: Router,
  alertController: AlertController,
  subscriptionService: StoreesSubscriptionService,
  firebaseAnalytics?: FirebaseAnalyticsService,
  options?: NavigateToLoadingScreenOptions
): Promise<boolean> {
  const navigationExtras = options?.navigationState
    ? { state: options.navigationState }
    : undefined;
  console.log(
    'navigateToLoadingScreen - start, subscribed:',
    subscriptionService.isSubscribed
  );
  // Check if user is subscribed and navigate directly to loading screen
  if (subscriptionService.isSubscribed) {
    const didNavigate = await router.navigate(
      [LOADING_SCREEN],
      navigationExtras
    );
    console.log(
      'navigateToLoadingScreen - subscribed direct navigation result:',
      didNavigate
    );
    return didNavigate;
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    void (async () => {
      console.log('navigateToLoadingScreen - presenting rewarded consent alert');
      const alert = await alertController.create({
        header: 'Get Your Story',
        message: 'Watch a short ad to unlock your story as your reward!',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              console.log('navigateToLoadingScreen - rewarded consent cancelled');
              firebaseAnalytics?.logStoryGetYourStoryCancel();
              settle(false);
              return true;
            },
          },
          {
            text: 'Continue',
            cssClass: 'primary',
            handler: async () => {
              console.log('navigateToLoadingScreen - rewarded consent continued');
              const didNavigate = await router.navigate(
                [LOADING_SCREEN],
                navigationExtras
              );
              console.log(
                'navigateToLoadingScreen - rewarded flow navigation result:',
                didNavigate
              );
              settle(didNavigate);
              return true;
            }
          }
        ],
        cssClass: 'reward-alert reward-alert-bottom-buttons',
      });

      await alert.present();
      console.log('navigateToLoadingScreen - rewarded consent alert presented');
      await alert.onDidDismiss();
      console.log('navigateToLoadingScreen - rewarded consent alert dismissed');
      settle(false);
    })();
  });
}

export type VoiceCloneMicrophonePermissionDialogOptions = {
  /** When true, mic access was denied or cannot be requested again. */
  permissionBlocked?: boolean;
};

/**
 * Explains why voice clone recording needs microphone access before the OS/browser prompt.
 * When blocked, explains that settings must be changed because permission cannot be re-requested.
 */
export async function showVoiceCloneMicrophonePermissionDialog(
  alertController: AlertController,
  options?: VoiceCloneMicrophonePermissionDialogOptions
): Promise<boolean> {
  const message = options?.permissionBlocked
    ? 'We need your microphone to record your voice and make an AI copy of it for your stories. You said no to the microphone, so we cannot record right now. Turn on microphone access for Storees in your device or browser settings, then come back here.'
    : 'We need your microphone to record your voice. We use that recording to make an AI copy of your voice for your stories.\n\nNext, your device will ask if Storees can use your microphone.\n\nTap Allow to keep going.';

  return new Promise((resolve) => {
    void alertController
      .create({
        header: 'Can we use your microphone?',
        message,
        cssClass: 'voice-clone-mic-permission-alert',
        buttons: options?.permissionBlocked
          ? [
              {
                text: 'OK',
                handler: () => resolve(false),
              },
            ]
          : [
              {
                text: 'Not now',
                role: 'cancel',
                handler: () => resolve(false),
              },
              {
                text: 'Continue',
                handler: () => resolve(true),
              },
            ],
      })
      .then((alert) => alert.present());
  });
}

export type DiscardRecordingConfirmationOptions = {
  /**
   * When true, explains that a voice already saved on device is not removed—only the in-progress
   * take is dropped (persisted slot audio is updated only on Save or removed only via delete).
   */
  keepSavedVoiceNote?: boolean;
};

/**
 * Shows a confirmation dialog for discarding the **current in-memory take** (session capture).
 * Does not delete persisted slot files; use {@link VoiceRecordingService.clearAfterDiscard} on confirm.
 */
export async function showDiscardRecordingConfirmation(
  alertController: AlertController,
  onConfirm: () => void | Promise<void>,
  options?: DiscardRecordingConfirmationOptions
): Promise<void> {
  const message = options?.keepSavedVoiceNote
    ? 'This will discard the recording you are working on now. A voice you already saved on this device stays until you replace it with Save or remove it from My Voices.'
    : 'Your current recording will be discarded. Are you sure you want to continue?';

  const alert = await alertController.create({
    header: 'Discard Recording',
    message,
    buttons: [
      {
        text: 'Cancel',
        role: 'cancel',
      },
      {
        text: 'OK',
        handler: async () => {
          await Promise.resolve(onConfirm());
        },
      },
    ],
  });

  await alert.present();
}

export function splitFilename(value: string) {
  return value.split('/').pop() ?? 'default.mp3';
}

/* ------------------------------------------------------------------
 * Native-file download & local cache
 * ---------------------------------------------------------------- */
export async function downloadAndSaveAudio(
  storyUrl: string,
  audioFileName: string,
  handleError?: (error: unknown) => void
) {
  try {
    if (!storyUrl) {
      throw new Error('No audio URL provided');
    }

    const response = await fetch(storyUrl);
    if (!response.ok) {
      throw new Error(
        `Failed to download audio: ${response.status} ${response.statusText}`
      );
    }

    const audioBlob = await response.blob();
    if (!audioBlob || audioBlob.size === 0) {
      throw new Error('Downloaded audio file is empty');
    }

    await saveAudio(audioBlob, audioFileName, handleError);
  } catch (err) {
    if (handleError) {
      handleError(err);
    } else {
      console.error('Error downloading audio:', err);
    }
  }
}

/* ------------------------------------------------------------------
 * Local-file save utilities
 * ---------------------------------------------------------------- */
export async function saveAudio(
  blob: Blob,
  audioFilename?: string,
  handleError?: (error: unknown) => void
) {
  if (!blob) return;

  // const metadata: Story = {
  //   storyTitle: this._storyTitle,
  //   storyText: this._storyText,
  //   storyVtt: this._storyVtt,
  //   imageUrl: this._storyImageUrl,
  //   audioUrl: this.storyUrl,
  //   // audioFilename: this.audioFileName,
  //   createdAt: this._storyCreatedAt,
  //   // savedStoryImageFilename: this._savedStoryImageFilename,
  // };
  // const metadataString = JSON.stringify(metadata);
  // const baseName = this.audioFileName.split('.').slice(0, -1).join('.');
  // const metadataFileName = `${baseName}.json`;

  try {
    await Filesystem.writeFile({
      path: `${STORIES_FILESYSTEM_PATH}/${audioFilename}`,
      data: await blobToBase64(blob),
      directory: Directory.Cache,
      recursive: true,
    });
  } catch (err) {
    if (handleError) {
      handleError(err);
    } else {
      console.error('Error saving audio:', err);
    }
  }
}

export function blobToBase64ForImage(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(blob);
  });
}

// Add this helper method for converting blobs to base64
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result as string;
      // Extract just the base64 part if it's a data URL
      const base64Content = base64data.split(',')[1] || base64data;
      resolve(base64Content);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function getFilenameFromUrl(url: string) {
  // Extract just the filename.jpg from the URL
  const urlParts = url.split('/');
  return urlParts[urlParts.length - 1]; // Gets the last part of the URL which is the filename with extension
}

export function isAudioFile(url: string) {
  return url.endsWith('.mp3');
}

export function isImageFile(url: string) {
  return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png');
}

export async function downloadImage(url: string, imageFileName: string) {
  // For binary files, we need to set the right options
  const options = {
    url,
    responseType: isImageFile(url) ? 'blob' : 'text',
  };

  const response = await CapacitorHttp.get(options as HttpOptions);
  if (response.status !== 200) {
    throw new Error(`File ${url} is not available. Status: ${response.status}`);
  }

  console.log('Received image data, saving to filesystem');

  try {
    // Convert data to base64 if it's not already
    let base64Data;
    if (response.data instanceof Blob) {
      // If data is a Blob (which it should be for images with responseType: 'blob')
      base64Data = await blobToBase64ForImage(response.data);
    } else if (typeof response.data === 'string') {
      // If somehow the data is returned as a base64 string already
      base64Data = response.data.startsWith('data:')
        ? response.data.split(',')[1]
        : response.data;
    } else {
      console.error('Unexpected data type:', typeof response.data);
      return;
    }

    // Save the file to the filesystem
    const result = await Filesystem.writeFile({
      path: `${STORIES_FILESYSTEM_PATH}/${imageFileName}`,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true,
    });
    console.log('Image saved to filesystem:', result.uri);

    return result;
  } catch (error) {
    console.error('Error saving image to filesystem:', error);
    return undefined;
  }
}

// Re-export calculateAge from date-utils to maintain backward compatibility
export { calculateAge } from './date-utils';
